/**
 * useShowcaseTimeline
 * 
 * Custom hook that runs the scripted UI animation timeline for the
 * SeatedShowcase component. Handles cursor movement, click simulation,
 * stage transitions, video-to-freeze swap, and full loop reset.
 * 
 * Returns a `start()` function that the component calls when the
 * background video has ended (or after a fallback timeout).
 */
import { useEffect, useRef, useCallback } from 'react';

export function useShowcaseTimeline(refs) {
  const isPlayingRef = useRef(false);
  const startTimeRef = useRef(0);
  const pausedAtRef = useRef(0);
  const frameRequestRef = useRef(null);
  const lastTriggeredRef = useRef(new Set());
  const cursorAnimRef = useRef(null);
  const timeoutsRef = useRef([]);

  // Helper: schedule a setTimeout and remember it for cleanup
  const setTrackedTimeout = useCallback((fn, ms) => {
    const id = setTimeout(fn, ms);
    timeoutsRef.current.push(id);
    return id;
  }, []);

  // Get center coords of element relative to stage
  const getCenter = useCallback((el) => {
    const stage = refs.stageRef.current;
    if (!stage || !el) return { x: 0, y: 0 };
    const stageRect = stage.getBoundingClientRect();
    const r = el.getBoundingClientRect();
    return {
      x: r.left - stageRect.left + r.width / 2,
      y: r.top - stageRect.top + r.height / 2,
    };
  }, [refs]);

  // Animate cursor to coords
  const moveCursor = useCallback((targetX, targetY, durationMs = 700) => {
    const cursor = refs.cursorRef.current;
    if (!cursor) return;
    if (cursorAnimRef.current) cancelAnimationFrame(cursorAnimRef.current);
    const startX = parseFloat(cursor.style.left) || window.innerWidth + 100;
    const startY = parseFloat(cursor.style.top) || window.innerHeight + 100;
    const t0 = performance.now();
    const step = (t) => {
      if (!isPlayingRef.current) return;
      const elapsed = (t - t0) / durationMs;
      const k = Math.min(1, elapsed);
      const eased = k < 0.5 ? 4 * k * k * k : 1 - Math.pow(-2 * k + 2, 3) / 2;
      cursor.style.left = (startX + (targetX - startX) * eased) + 'px';
      cursor.style.top = (startY + (targetY - startY) * eased) + 'px';
      if (k < 1) cursorAnimRef.current = requestAnimationFrame(step);
    };
    cursorAnimRef.current = requestAnimationFrame(step);
  }, [refs]);

  const moveCursorTo = useCallback((el, durationMs = 700) => {
    if (!el) return;
    const c = getCenter(el);
    moveCursor(c.x, c.y, durationMs);
  }, [getCenter, moveCursor]);

  const clickCursor = useCallback(() => {
    const cursor = refs.cursorRef.current;
    if (!cursor) return;
    cursor.classList.add('is-clicking');
    setTrackedTimeout(() => cursor.classList.remove('is-clicking'), 600);
  }, [refs, setTrackedTimeout]);

  const pressButton = useCallback((btn) => {
    if (!btn) return;
    btn.classList.add('is-pressed');
    setTrackedTimeout(() => btn.classList.remove('is-pressed'), 250);
  }, [setTrackedTimeout]);

  const setActiveStep = useCallback((stepName) => {
    const indicator = refs.stepIndicatorRef.current;
    if (!indicator) return;
    indicator.querySelectorAll('.step').forEach((s) => {
      s.classList.remove('is-active', 'is-done');
    });
    const order = ['map', 'seat', 'qr', 'scan'];
    const idx = order.indexOf(stepName);
    indicator.querySelectorAll('.step').forEach((s, i) => {
      if (i < idx) s.classList.add('is-done');
      if (i === idx) s.classList.add('is-active');
    });
  }, [refs]);

  const highlightTier = useCallback((tier) => {
    const stage = refs.stageRef.current;
    if (!stage) return;
    stage.querySelectorAll('.section-block').forEach((s) => {
      s.classList.remove('is-tier-hovered', 'is-section-hovered', 'is-selected', 'is-dimmed');
      if (s.dataset.tier === String(tier)) {
        s.classList.add('is-tier-hovered');
      } else {
        s.classList.add('is-dimmed');
      }
    });
  }, [refs]);

  const clearTierHighlight = useCallback(() => {
    const stage = refs.stageRef.current;
    if (!stage) return;
    stage.querySelectorAll('.section-block').forEach((s) => {
      s.classList.remove('is-tier-hovered', 'is-dimmed');
    });
  }, [refs]);

  const lockTier = useCallback((tier) => {
    const stage = refs.stageRef.current;
    if (!stage) return;
    stage.querySelectorAll('.section-block').forEach((s) => {
      s.classList.remove('is-tier-hovered');
      if (s.dataset.tier === String(tier)) {
        s.classList.remove('is-dimmed');
      } else {
        s.classList.add('is-dimmed');
      }
    });
  }, [refs]);

  const hoverSection = useCallback((id) => {
    const stage = refs.stageRef.current;
    const tooltip = refs.sectionTooltipRef.current;
    if (!stage || !tooltip) return;
    stage.querySelectorAll('.section-block').forEach((s) => s.classList.remove('is-section-hovered'));
    const s = stage.querySelector(`.section-block[data-id="${id}"]`);
    if (!s) return;
    s.classList.add('is-section-hovered');
    const stageRect = stage.getBoundingClientRect();
    const sRect = s.getBoundingClientRect();
    tooltip.style.left = (sRect.left - stageRect.left + sRect.width / 2) + 'px';
    tooltip.style.top = (sRect.top - stageRect.top + sRect.height / 2) + 'px';
    tooltip.classList.add('is-shown');
  }, [refs]);

  const selectSection = useCallback((id) => {
    const stage = refs.stageRef.current;
    const tooltip = refs.sectionTooltipRef.current;
    const detail = refs.sectionDetailRef.current;
    if (!stage) return;
    stage.querySelectorAll('.section-block').forEach((s) => s.classList.remove('is-selected'));
    const s = stage.querySelector(`.section-block[data-id="${id}"]`);
    if (!s) return;
    s.classList.add('is-selected');
    s.classList.remove('is-section-hovered');
    if (tooltip) tooltip.classList.remove('is-shown');
    if (detail) detail.classList.add('is-shown');
  }, [refs]);

  // Build timeline lazily so we can access refs
  const buildTimeline = useCallback(() => {
    const r = refs;
    return [
      { t: 0.5, fn: () => r.initialMarkerRef.current?.classList.add('is-shown') },

      { t: 2.0, fn: () => {
        if (r.stageRef.current) r.stageRef.current.dataset.step = 'map';
        setActiveStep('map');
      }},

      { t: 3.5, fn: () => {
        const cursor = r.cursorRef.current;
        if (!cursor) return;
        cursor.style.left = (window.innerWidth + 100) + 'px';
        cursor.style.top = (window.innerHeight - 80) + 'px';
        cursor.classList.add('is-shown');
      }},

      // Hover Bronze → Silver → Gold → VIP
      { t: 4.2, fn: () => moveCursorTo(r.stageRef.current?.querySelector('#chip1'), 800) },
      { t: 5.0, fn: () => {
        r.stageRef.current?.querySelector('#chip1')?.classList.add('is-hovered');
        highlightTier(1);
      }},
      { t: 5.9, fn: () => {
        r.stageRef.current?.querySelector('#chip1')?.classList.remove('is-hovered');
        moveCursorTo(r.stageRef.current?.querySelector('#chip2'), 600);
      }},
      { t: 6.4, fn: () => {
        r.stageRef.current?.querySelector('#chip2')?.classList.add('is-hovered');
        highlightTier(2);
      }},
      { t: 7.3, fn: () => {
        r.stageRef.current?.querySelector('#chip2')?.classList.remove('is-hovered');
        moveCursorTo(r.stageRef.current?.querySelector('#chip3'), 600);
      }},
      { t: 7.8, fn: () => {
        r.stageRef.current?.querySelector('#chip3')?.classList.add('is-hovered');
        highlightTier(3);
      }},
      { t: 8.7, fn: () => {
        r.stageRef.current?.querySelector('#chip3')?.classList.remove('is-hovered');
        moveCursorTo(r.stageRef.current?.querySelector('#chip4'), 600);
      }},
      { t: 9.2, fn: () => {
        r.stageRef.current?.querySelector('#chip4')?.classList.add('is-hovered');
        highlightTier(4);
      }},
      // Click VIP filter
      { t: 10.0, fn: () => {
        clickCursor();
        const c = r.stageRef.current?.querySelector('#chip4');
        if (c) {
          c.classList.add('is-pressed');
          setTrackedTimeout(() => c.classList.remove('is-pressed'), 250);
        }
      }},
      { t: 10.3, fn: () => {
        const c = r.stageRef.current?.querySelector('#chip4');
        c?.classList.remove('is-hovered');
        c?.classList.add('is-active');
        lockTier(4);
      }},

      // Move to VIP-N3 section
      { t: 11.0, fn: () => {
        const s = r.stageRef.current?.querySelector('.section-block[data-id="VIP-N3"]');
        moveCursorTo(s, 1100);
      }},
      { t: 12.0, fn: () => hoverSection('VIP-N3') },

      // Click VIP-N3 → side panel
      { t: 13.5, fn: () => clickCursor() },
      { t: 13.7, fn: () => selectSection('VIP-N3') },

      // Move to "Seat selection" CTA
      { t: 14.8, fn: () => moveCursorTo(r.sdCtaRef.current, 900) },
      { t: 15.7, fn: () => {
        clickCursor();
        pressButton(r.sdCtaRef.current);
      }},

      // Step 2: Pick your seat
      { t: 16.0, fn: () => {
        if (r.stageRef.current) r.stageRef.current.dataset.step = 'seats-row';
        setActiveStep('seat');
        r.sectionDetailRef.current?.classList.remove('is-shown');
      }},
      { t: 17.0, fn: () => {
        if (r.stageRef.current) r.stageRef.current.dataset.step = 'seat-detail';
      }},

      // Cursor → Select seat button
      { t: 18.5, fn: () => moveCursorTo(r.btnPrimaryRef.current, 1000) },

      // Click Select seat
      { t: 19.7, fn: () => {
        clickCursor();
        pressButton(r.btnPrimaryRef.current);
      }},
      { t: 19.9, fn: () => {
        if (r.stageRef.current) r.stageRef.current.dataset.step = 'seat-selected';
        if (r.pricebarTitleRef.current) r.pricebarTitleRef.current.textContent = 'Selected';
        if (r.pricebarSubRef.current) r.pricebarSubRef.current.textContent = 'VIP North 3 \u00B7 Row 12, Seat 23';
        if (r.btnPrimaryLabelRef.current) r.btnPrimaryLabelRef.current.textContent = 'Continue to checkout';
        if (r.btnCloseRef.current) r.btnCloseRef.current.disabled = true;
      }},

      // Step 3: Get your QR
      { t: 21.5, fn: () => moveCursorTo(r.btnPrimaryRef.current, 800) },
      { t: 22.3, fn: () => {
        clickCursor();
        pressButton(r.btnPrimaryRef.current);
      }},
      { t: 22.6, fn: () => {
        if (r.stageRef.current) r.stageRef.current.dataset.step = 'ticket';
        setActiveStep('qr');
      }},
      { t: 24.5, fn: () => moveCursorTo(r.ticketBtnSaveRef.current, 900) },
      { t: 25.5, fn: () => {
        clickCursor();
        pressButton(r.ticketBtnSaveRef.current);
      }},
      { t: 25.7, fn: () => {
        if (r.stageRef.current) r.stageRef.current.dataset.step = 'ticket-saved';
        r.qrCardRef.current?.classList.add('is-saved');
      }},

      // Step 4: Scan & enter
      { t: 27.5, fn: () => {
        if (r.stageRef.current) r.stageRef.current.dataset.step = 'scan';
        setActiveStep('scan');
        r.cursorRef.current?.classList.remove('is-shown');
      }},
      { t: 30.0, fn: () => {
        if (r.stageRef.current) r.stageRef.current.dataset.step = 'scan-success';
      }},

      // Restart loop
      { t: 34.5, fn: () => reset() },
    ];
  }, [refs, setActiveStep, moveCursorTo, clickCursor, pressButton, highlightTier,
      lockTier, hoverSection, selectSection, setTrackedTimeout]);

  const timelineRef = useRef(null);

  const tick = useCallback(() => {
    if (!isPlayingRef.current) return;
    const elapsed = (performance.now() - startTimeRef.current) / 1000;
    if (refs.timerRef.current) {
      refs.timerRef.current.textContent = elapsed.toFixed(1) + 's';
    }
    if (timelineRef.current) {
      timelineRef.current.forEach((event, i) => {
        if (elapsed >= event.t && !lastTriggeredRef.current.has(i)) {
          lastTriggeredRef.current.add(i);
          try { event.fn(); } catch (e) { console.error(e); }
        }
      });
    }
    frameRequestRef.current = requestAnimationFrame(tick);
  }, [refs]);

  const play = useCallback(() => {
    if (isPlayingRef.current) return;
    if (!timelineRef.current) timelineRef.current = buildTimeline();
    isPlayingRef.current = true;
    startTimeRef.current = performance.now() - pausedAtRef.current * 1000;
    tick();
  }, [buildTimeline, tick]);

  const pause = useCallback(() => {
    if (!isPlayingRef.current) return;
    pausedAtRef.current = (performance.now() - startTimeRef.current) / 1000;
    isPlayingRef.current = false;
    if (frameRequestRef.current) cancelAnimationFrame(frameRequestRef.current);
  }, []);

  const resetStageState = useCallback(() => {
    pause();
    pausedAtRef.current = 0;
    lastTriggeredRef.current.clear();
    // Clear pending timeouts
    timeoutsRef.current.forEach((id) => clearTimeout(id));
    timeoutsRef.current = [];
    if (cursorAnimRef.current) cancelAnimationFrame(cursorAnimRef.current);

    const r = refs;
    if (r.stageRef.current) r.stageRef.current.dataset.step = 'freeze';
    r.initialMarkerRef.current?.classList.remove('is-shown');
    if (r.cursorRef.current) {
      r.cursorRef.current.classList.remove('is-shown', 'is-clicking');
      r.cursorRef.current.style.left = '-100px';
      r.cursorRef.current.style.top = '-100px';
    }
    if (r.pricebarTitleRef.current) r.pricebarTitleRef.current.textContent = 'VIP North 3';
    if (r.pricebarSubRef.current) r.pricebarSubRef.current.textContent = 'Premium \u00B7 Lounge access included';
    if (r.btnPrimaryLabelRef.current) r.btnPrimaryLabelRef.current.textContent = 'Select seat';
    if (r.btnCloseRef.current) r.btnCloseRef.current.disabled = false;
    r.btnPrimaryRef.current?.classList.remove('is-pressed');
    r.sdCtaRef.current?.classList.remove('is-pressed');

    const stage = r.stageRef.current;
    if (stage) {
      stage.querySelectorAll('.filter-chip').forEach((c) => {
        c.classList.remove('is-active', 'is-pressed', 'is-hovered');
      });
      stage.querySelectorAll('.section-block').forEach((s) => {
        s.classList.remove('is-section-hovered', 'is-selected', 'is-dimmed', 'is-tier-hovered');
      });
    }
    r.sectionTooltipRef.current?.classList.remove('is-shown');
    r.sectionDetailRef.current?.classList.remove('is-shown');
    r.qrCardRef.current?.classList.remove('is-saved');
    r.ticketBtnSaveRef.current?.classList.remove('is-pressed');
    r.ticketBtnWalletRef.current?.classList.remove('is-pressed');
    setActiveStep('');
  }, [pause, refs, setActiveStep]);

  const reset = useCallback(() => {
    resetStageState();
    // Restart the video for next loop
    const bgVideo = refs.bgVideoRef.current;
    const freezeImg = refs.freezeImgRef.current;
    if (bgVideo && freezeImg) {
      bgVideo.style.display = 'block';
      freezeImg.style.display = 'none';
      bgVideo.currentTime = 0;
      bgVideo.play().catch(() => {});
    } else {
      setTrackedTimeout(play, 400);
    }
  }, [play, refs, resetStageState, setTrackedTimeout]);

  const prepareIntro = useCallback(() => {
    resetStageState();
    const bgVideo = refs.bgVideoRef.current;
    const freezeImg = refs.freezeImgRef.current;
    if (!bgVideo || !freezeImg) return;

    bgVideo.style.display = 'block';
    freezeImg.style.display = 'none';
    try {
      bgVideo.pause();
      bgVideo.currentTime = 0;
    } catch (e) {
      // Some browsers can reject currentTime updates before metadata is ready.
    }
  }, [refs, resetStageState]);

  // Start the showcase: hide video, show freeze frame, run timeline
  const start = useCallback(() => {
    const bgVideo = refs.bgVideoRef.current;
    const freezeImg = refs.freezeImgRef.current;
    if (bgVideo && freezeImg) {
      bgVideo.style.display = 'none';
      freezeImg.style.display = 'block';
      try { bgVideo.pause(); } catch (e) { /* ignore */ }
    }
    play();
  }, [refs, play]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      isPlayingRef.current = false;
      if (frameRequestRef.current) cancelAnimationFrame(frameRequestRef.current);
      if (cursorAnimRef.current) cancelAnimationFrame(cursorAnimRef.current);
      timeoutsRef.current.forEach((id) => clearTimeout(id));
    };
  }, []);

  return { start, play, pause, reset, prepareIntro };
}
